// src/pages/CartPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext'; // Import the cart context
import ProductOptionsModal from '../components/ProductOptionsModal'; // You can reuse your modal

import { CartItem, OrderItem, Tables, CartPageProps } from '../utils/types'; // Adjust based on your setup
import { displayPrice, generateUniqueKey4ChkOut } from '../utils/general';
import { useSettings } from '../context/SettingsContext';
import { useNavigate, useLocation } from 'react-router-dom';

import { fetchOrderDetails, updateTableStatus, createOrder, updateOrder } from '../utils/api';
// import { OrderSummary } from '../utils/types';
// import { table } from 'console';
// import { useSearchParams } from "react-router-dom"; // If using react-router-dom v6+
// import { generateUniqueKey  } from '../utils/general';
import { useOrderType } from '../hooks/useOrderType';
import { v4 as uuidv4 } from 'uuid'; // Import the UUID library

const CartPage: React.FC<CartPageProps> = ({ setOrderTypeModalOpen, variant = 'standalone' }) => {

    const cartSummaryClass =
        variant === 'standalone' ? 'w-full' : 'w-5/12';

    const isInitialRender = useRef(true); // Track the initial render to prevent unnecessary `clearCart`
    const location = useLocation();

    const { settings } = useSettings();
    const navigate = useNavigate();

    const { cartItems, updateQuantity, removeFromCart, clearCart, setCartItems } = useCart();

    // State for order type and table
    // const { updateOrderType } = useOrderType();

    // const [orderType, setOrderType] = useState<string | null>(
    //     localStorage.getItem('orderType') || null
    // );


    const { orderType, updateOrderType } = useOrderType();

    const [selectedTable, setSelectedTable] = useState<Tables | null>(null);
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState<number | null>(null);

    // Ensure orderType is stored in localStorage
    useEffect(() => {
        if (orderType) {
            localStorage.setItem('orderType', orderType);
            updateOrderType(orderType);
        }
    }, [orderType]);

    // Ensure selectedTable is stored in localStorage
    useEffect(() => {
        if (selectedTable) {
            localStorage.setItem('selectedTable', JSON.stringify(selectedTable));
        }
    }, [selectedTable]);

    // Retrieve table and orderType from localStorage on load
    useEffect(() => {
        const storedTable = localStorage.getItem('selectedTable');
        if (storedTable) {
            setSelectedTable(JSON.parse(storedTable) as Tables);
        }
    }, []);

    // Rules for Dine In and Takeaway
    useEffect(() => {
        if (orderType === 'Dine In' && !selectedTable) {
            // console.warn('A table must be selected for Dine In orders.');
        } else if (orderType === 'Takeaway') {
            setSelectedTable(null); // Dummy table for Takeaway
        }
        // updateOrderType(orderType);

    }, [orderType, selectedTable]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlOrderId = searchParams.get('orderId');
        const localStorageOrderId = localStorage.getItem('orderId');

        // Skip clearCart on the first render to prevent infinite loop
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }

        if (urlOrderId) {
            setOrderId(parseInt(urlOrderId));
            localStorage.setItem('orderId', urlOrderId); // Save it for persistence
        } else if (localStorageOrderId) {
            setOrderId(parseInt(localStorageOrderId));
        } else {
            // clearCart(); // Clear cart if there's no orderId
        }
    }, [location.search, clearCart]); // Dependencies remain the same

    const prevCartItems = useRef(cartItems); // store previous cartItems state
    const fetchedOnce = useRef(false); // track if fetch has happened

    // Fetch order details if orderId is present
    useEffect(() => {

        // Check if there is no orderId or session is new
        if (!orderId) {
            return; // Don't attempt fetching if no orderId
        }

        const fetchOrder = async () => {
            // console.log(orderId);
            if (!orderId || !settings) return;

            setLoading(true);

            fetchedOnce.current = true; // Mark as fetched once

            try {
                const orderDetails = await fetchOrderDetails(settings.vendorId, orderId);

                // console.log("Fetched Order Details:", orderDetails);

                // Set order details
                // setOrder(orderDetails);

                // Check if table is a JSON string or an object
                if (orderDetails?.table) {
                    const parsedTable =
                        typeof orderDetails.table === "string"
                            ? JSON.parse(orderDetails.table)
                            : orderDetails.table;

                    // Convert keys to camelCase
                    const camelCaseTable = convertToCamelCase(parsedTable);
                    // console.log("Parsed Table Details (Camel Case):", camelCaseTable);

                    setSelectedTable(camelCaseTable as Tables);
                }

                // Set order type if available
                if (orderDetails?.order_type) {
                    // console.log("Order Type:", orderDetails.order_type);
                    updateOrderType(orderDetails.order_type);
                }

                // console.log("new Cart Items:", cartItems);

                // Load items into the cart
                if (orderDetails?.items) {
                    const cartItemsFromOrder = orderDetails.items.map((item: OrderItem) => ({
                        // productKey: `${item.product_id}_${item.add_ons}_${item.toppings}`, // Generate unique key
                        // productKey: generateUniqueKey(item.product.id, ''), // + '_chkout', // Generate unique key
                        productKey: generateUniqueKey4ChkOut(item.product.id, '')  + '_chkout', // Generate unique key for checked out
                        productId: item.product_id,
                        vendorId: item.vendor_id,
                        name: item.name,
                        price: parseFloat(item.price),
                        quantity: item.quantity,
                        options: {
                            addOns: item.add_ons,
                            toppings: item.toppings,
                            specifications: item.specifications,
                        },
                        totalPrice: parseFloat(item.total_price),
                        product: item.product,

                        checkedOut: true, // Mark as checked out

                    }));

                    // console.log("Fetched Cart Items (Checked Out):", cartItemsFromOrder);

                    // Add items to cart (prevents duplicate additions by checking for `checkedOut` items)
                    // if (cartItems.length === 0 || prevCartItems.current !== cartItems) {
                    // Prevent redundant updates to cartItems
                    if (prevCartItems.current !== cartItemsFromOrder) {
                        setCartItems((prevItems) => [
                            ...prevItems.filter((item) => !item.checkedOut), // Only add non-checked out items
                            ...cartItemsFromOrder,
                        ]);
                    }

                    // Optionally, store checked-out items in localStorage for persistence
                    localStorage.setItem('checkedOutItems', JSON.stringify(cartItemsFromOrder));
                    localStorage.setItem('existingOrderId', String(orderId));

                    prevCartItems.current = cartItemsFromOrder; // Update reference to the new items

                }


            } catch (error) {
                console.error("Error fetching order details:", error);
            } finally {
                setLoading(false);
            }
        };

        const searchParams = new URLSearchParams(location.search);

        if (!fetchedOnce.current && (cartItems.length === 0
            || searchParams.get('orderId')
            || localStorage.getItem('orderId'))) {
            fetchOrder();
        }

    }, [orderId, settings, setCartItems, location, cartItems.length, updateOrderType]); // Dependencies include orderId and settings

    const convertToCamelCase = (obj: Record<string, any>): Record<string, any> => {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            const camelCaseKey = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
            acc[camelCaseKey] = value;
            return acc;
        }, {} as Record<string, any>);
    };

    // Clear Cart Button Handler
    const handleClearCart = () => {
        clearCart();
        localStorage.removeItem('checkedOutItems'); // Remove checked-out items if stored separately
        localStorage.removeItem('selectedTable'); // Optionally clear any session-based selections
    };

    // New Session Button Handler
    // Clear Cart and reset session
    const handleNewSession = () => {
        clearCart();
        // Clear relevant session data
        localStorage.removeItem('existingOrderId');
        localStorage.removeItem('checkedOutItems');
        localStorage.removeItem('selectedTable');
        localStorage.removeItem('orderId');

        // Explicitly reset state to ensure no leftover values
        setOrderId(null);  // Assuming `orderId` is a state variable
        setCartItems([]);   // Clear the cart

        // Optionally reset any other relevant states
        setSelectedTable(null);
        updateOrderType('');

        navigate('/tables'); // Redirect to a page (home or otherwise) to refresh the session
    };


    //store checkedOutItems

    const [checkedOutItems, setCheckedOutItems] = useState<CartItem[]>([]);

    // console.log('checkedOutItems', checkedOutItems);

    useEffect(() => {
        // Retrieve checked-out items from local storage
        const storedCheckedOutItems = JSON.parse(localStorage.getItem('checkedOutItems') || '[]');
        setCheckedOutItems(storedCheckedOutItems);
    }, []);

    const isCheckedOut = (productKey: string) => {
        const checkedOutItems = JSON.parse(localStorage.getItem('checkedOutItems') || '[]');
        return checkedOutItems.some((item: { productKey: string }) => item.productKey === productKey);
    };

    // console.log(JSON.stringify(cartItems));
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);


    // Open Order Type Modal
    const openOrderTypeModal = () => {

        setOrderTypeModalOpen(true); // Open the order type modal

    };

    useEffect(() => {
        const calculateTotals = () => {
            const newTotalQuantity = cartItems.reduce((acc, item) => acc + item.quantity, 0);
            const newTotalPrice = cartItems.reduce((acc, item) => acc + item.totalPrice, 0);
            setTotalQuantity(newTotalQuantity);
            setTotalPrice(newTotalPrice);
        };

        calculateTotals();
    }, [cartItems]);
    // ...

    const [selectedCartItem, setSelectedCartItem] = React.useState<CartItem | null>(null); // To store the item being edited

    const [modalOpen, setModalOpen] = useState(false); // State for modal visibility

    const handleEdit = (cartItem: CartItem) => {
        setSelectedCartItem(cartItem); // Set the selected cart item
        // console.log(cartItem, null, "\t");

        setModalOpen(true); // Open the modal
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedCartItem(null); // Reset the selected item after closing
    };

    // Updated canCheckout condition
    const canCheckout =
        cartItems.length > 0 && // Ensure cart is not empty
        orderType !== 'You need select an order type' && // Ensure orderType is set
        (
            (orderType === 'Dine In' && selectedTable?.id && !isNaN(Number(selectedTable.id))) || // If Dine In, selectedTable must be valid
            (orderType === 'Takeaway') // If Takeaway, selectedTable can be null or any value
        ) &&
        cartItems.some(item => !isCheckedOut(item.productKey)); // Ensure at least one item is new and not checked out



    // State to hold the current date and time
    const [currentDateTime, setCurrentDateTime] = useState(new Date());

    // useEffect(() => {
    //     // Update date and time every second
    //     const timer = setInterval(() => {
    //         setCurrentDateTime(new Date());
    //     }, 1000);

    //     // Clear interval on component unmount
    //     return () => clearInterval(timer);
    // }, []);

    // Format date and time
    const formattedDateTime = currentDateTime.toLocaleString('en-GB', {
        // weekday: 'long',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
    });

    useEffect(() => {
        const storedOrderId = localStorage.getItem('orderId');
        if (storedOrderId && storedOrderId !== 'null') {
            setOrderId(parseInt(storedOrderId));
        }
    }, []);

    // Listen for changes to localStorage (triggered by `storage` event)
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'orderId') {
                const newOrderId = event.newValue ? parseInt(event.newValue) : null;
                setOrderId(newOrderId); // Update state to reflect the new value
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);
    // console.log("Order Type:", orderType);
    // console.log("Selected Table:", selectedTable);


    // abb 241208 handle direct checkout here, taken from checkout.tsx
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const vendorId = JSON.parse(localStorage.getItem('user') || '{}').vendor_id;
    const token = localStorage.getItem('token');
    const customerId = 0; // Set actual customer ID if available
    // const orderType = localStorage.getItem('orderType') || 'You need to select an order type';
    // const orderId: any = localStorage.getItem('orderId');

    useEffect(() => {
        if (orderType === 'Takeaway') {
            setSelectedTable(null);
        }
    }, [orderType]);

    const handleCheckout = async () => {
        if (!cartItems || cartItems.length === 0) {
            setError("Your cart is empty. Please add items before checking out.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            if (!vendorId || !token) {
                throw new Error("Missing vendor ID or authorization token");
            }

            const items = cartItems.map(item => ({
                productId: item.productId,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                addons: item.options.add_ons?.map(addon => addon.name).join(", ") || "",
                addonDetails: Array.isArray(item.options.add_ons)
                    ? item.options.add_ons.map(addon => ({
                        name: addon.name,
                        price: addon.price,
                    }))
                    : [], // Ensure add_ons is an array
                toppings: Array.isArray(item.options.toppings)
                    ? item.options.toppings.map(topping => topping.name).join(", ")
                    : "", // Ensure toppings is an array and join them
                toppingDetails: Array.isArray(item.options.toppings)
                    ? item.options.toppings.map(topping => ({
                        name: topping.name,
                        price: topping.price,
                    }))
                    : [], // Ensure toppings is an array
                specifications: Array.isArray(item.options.specifications)
                    ? item.options.specifications.map(spec => ({
                        specification_id: spec.keyid,
                        option_id: spec.keyid,
                        option_name: spec.name,
                        extra_cost: spec.price,
                    }))
                    : [], // Ensure specifications is an array
                totalPrice: item.totalPrice,
            }));

            const payload = {
                customerId: customerId,
                orderType: orderType,
                tableId: selectedTable?.id || selectedTable,
                items: items,
                status: 'pending',
                amountToPay: cartItems.reduce((acc, item) => acc + item.totalPrice, 0),
                userId: vendorId,
                table: selectedTable,
            };

            let response;

            if (orderId) {
                response = await updateOrder(vendorId, Number(orderId), payload, token);
            } else {
                response = await createOrder(vendorId, payload, token);
            }

            if (response.status === 200) {
                const newOrderId = response.data.id || orderId;

                // console.log("Order created/updated successfully with ID:", newOrderId);
                // console.log("b4 updateTableStatus:", selectedTable);
                if (orderType === 'Dine In' && selectedTable?.id) {
                    try {
                        await updateTableStatus(vendorId, selectedTable.id, 'occupied', newOrderId, customerId);
                    } catch (err) {
                        console.error("Error in updateTableStatus:", err);
                    }
                }
                // console.log("after updateTableStatus:", selectedTable);

                const checkedOutItems = cartItems.map(item => ({ ...item, checkedOut: true }));
                localStorage.setItem('checkedOutItems', JSON.stringify(checkedOutItems));
                localStorage.setItem('existingOrderId', newOrderId);

                clearCart();

                // navigate(`/order-details/${newOrderId}`); // abb 250108

                if (orderType === 'Dine In') {
                    handleNewSession(); // Clear cart and reset session and navigate to tables selection abb 250108
                } else {
                    navigate('/payment/' + newOrderId); // Navigate to root for takeaway orders
                }
                
            } else {
                throw new Error("Order creation/update failed with status: " + response.status);
            }
        } catch (err: any) {
            console.error("Checkout error:", err);
            setError(err.response?.data?.message + " - An error occurred while processing your order.");
        } finally {
            setIsLoading(false);
        }
    };


    return (
        // <div className="app-page flex flex-wrap ">
        <div className="app-page">

            <div className='cart-topbar'>

                <div className="w-3/5 font-normal mb-0 flex flex-row text-xs">

                    <span className="m-0 p-0 mr-2 text-red-500">
                        {localStorage.getItem('orderId') && localStorage.getItem('orderId') !== 'null' ? (
                            <span onClick={() => navigate(`/order-details/${orderId}`)} className='cursor-pointer underline underline-offset-4'>
                                Order #{localStorage.getItem('orderId')}
                            </span>
                        ) : (
                            <>New Order</>
                        )}
                    </span>
                    <span className="m-0 p-0">
                        {formattedDateTime}
                    </span>
                </div>

                {/* Order Type and Table Selection */}
                <div className="order-details w-2/5 flex flex-row justify-end text-sm">
                    <span onClick={openOrderTypeModal} className="m-0 p-0 mr-4  font-bold text-purple-700">
                        {orderType || 'Select Order Type'}
                    </span>

                    {/* Table Selection - Visible Only for Dine In */}
                    {orderType === 'Dine In' && (
                        <span onClick={() => navigate('/tables')} className="m-0 p-0">
                            <p>Table: <span className='font-bold text-purple-700'>{selectedTable?.tableName || 'None'}</span></p>
                            {/* Include logic to open table selection modal if needed */}
                        </span>
                    )}
                </div>

            </div>

            <div className='w-full overflow-x-hidden overflow-y-auto mt-22 pb-56'>

                {checkedOutItems.length > 0 && (

                    <div className="w-full border border-gray-500 bg-gray-50 mb-5 p-2">
                        {localStorage.getItem('orderId') && localStorage.getItem('orderId') !== 'null' ? (
                            <>
                                <span>Continue on </span>
                                <span className="font-bold underline underline-offset-4 text-red-700" onClick={() => navigate(`/order-details/${orderId}`)}>
                                    Order #{localStorage.getItem('orderId')}
                                </span>
                                <span>
                                    {` - ${checkedOutItems.filter(item => item.checkedOut).length} items (${checkedOutItems
                                        .filter(item => item.checkedOut)
                                        .reduce((acc, item) => acc + item.quantity, 0)} total processed)`}
                                </span>
                            </>
                        ) : (
                            <span className="text-red-500">New Order</span>
                        )}
                    </div>
                )}

                <div>
                    {cartItems.length === 0 ? (
                        <p className=' flex flex-row justify-center pt-10 text-center text-red-500'>Your cart is empty.</p>
                    ) : (
                        <ol className='list-decimal  hover:list-disc px-2'>
                            {cartItems.map((item, index) => (

                                <li key={item.productKey || index}
                                    className={`border-b py-2 ${isCheckedOut(item.productKey) ? 'hidden' : ''}`} // Hide if checked out

                                >
                                    {/* <li key={`${item.productId}-${JSON.stringify(item.options)}` || index} className="border-b py-2"> */}

                                    <h4 className="text-sm font-semibold">
                                        {/* Product : */}
                                        {/* {item.productKey} - */}
                                        {/* {item.productId} - */}
                                        {item.name} ({settings?.currencyName}{displayPrice(item.price)})

                                        {/* ({item.productKey}) */}

                                        {isCheckedOut(item.productKey) && (
                                            <span className="text-red-500"> (Checked Out)</span>
                                        )}

                                    </h4>
                                    {/* <h4 className="text-md font-semibold">Product : {item.productId} - {item.name} :{item.price} ({item.productId})</h4> */}

                                    <ul className='text-sm list-disc ml-5'>
                                        {/* Display Add-ons */}
                                        {Array.isArray(item.options.add_ons) &&
                                            item.options.add_ons.map((opt: any) => (
                                                <li key={uuidv4() + opt.key}>
                                                    {opt.name}
                                                    {opt.price > 0 && (
                                                        <>&nbsp;( +{settings?.currencyName}{displayPrice(opt.price)})</>
                                                    )}
                                                </li>
                                            ))}
                                        {/* Display Toppings */}
                                        {Array.isArray(item.options.toppings) &&
                                            item.options.toppings.map((opt: any) => (
                                                <li key={uuidv4() + opt.key}>
                                                    {opt.name}
                                                    {/* (+{settings?.currencyName}{displayPrice(opt.price)}) */}
                                                    {opt.price > 0 && (
                                                        <>&nbsp;( +{settings?.currencyName}{displayPrice(opt.price)})</>
                                                    )}
                                                </li>
                                            ))}
                                        {/* Display Specifications */}
                                        {Array.isArray(item.options.specifications) &&
                                            item.options.specifications.map((specOpt: any) => (
                                                <li key={uuidv4() + specOpt.key}>
                                                    {specOpt.name}
                                                    {/* (+{settings?.currencyName}{displayPrice(specOpt.price)}) */}
                                                    {specOpt.price > 0 && (
                                                        <>&nbsp;( +{settings?.currencyName}{displayPrice(specOpt.price)})</>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>

                                    <div className='flex flex-row justify-end'>
                                        <div className='flex flex-row justify-start'>
                                            <p className='text-xs pt-3 px-2'>Quantity:</p>

                                            <button className="AdjBtn" onClick={() => updateQuantity(item.productKey, item.quantity - 1)}
                                                disabled={isCheckedOut(item.productKey)}
                                            >
                                                -
                                            </button>

                                            <p className='pt-3 px-2'>{item.quantity}</p>

                                            <button className="AdjBtn" onClick={() => updateQuantity(item.productKey, item.quantity + 1)}
                                                disabled={isCheckedOut(item.productKey)}
                                            >
                                                +
                                            </button>
                                        </div>

                                        {/* <p className='  font-semibold'>{settings?.currencyName}{item.totalPrice}</p> */}
                                        <p className='pl-4 pr-2 pt-3 text-sm  font-semibold'>{settings?.currencyName}{(item.totalPrice).toFixed(2)}</p>

                                    </div>

                                    {!isCheckedOut(item.productKey) && (

                                        <div className='flex' key={item.productKey}>
                                            {/* <div className='flex' key={item.productId}> */}
                                            <button className="ActionBtnXS w-12 bg-green-600" onClick={() => handleEdit(item)}
                                                disabled={isCheckedOut(item.productKey)}
                                            >
                                                Edit
                                                {/* {item.productKey} */}
                                            </button>


                                            <button className="ActionBtnXS w-12  bg-red-600 " onClick={() => removeFromCart(item.productKey)}
                                                disabled={isCheckedOut(item.productKey)}
                                            >
                                                Remove
                                            </button>
                                        </div>

                                    )}
                                </li>

                            ))}
                        </ol>

                    )}
                </div>

            </div >


            <div className={`cart-summary-part  ${cartSummaryClass}`} >
                <div className="w-full flex justify-between mb-4 ">

                    <div className='flex flex-col'>
                        <p className='text-xs  font-bold'>Current Quantity: {cartItems.filter(item => !isCheckedOut(item.productKey)).reduce((acc, item) => acc + item.quantity, 0)}</p> {/* Display quantity */}
                        <p className='text-xs  font-normal pt-1' style={{ fontSize: '0.6rem' }}>Quantity in this Order : <br />{cartItems.filter(item => item.checkedOut).reduce((acc, item) => acc + item.quantity, 0)} processed / {totalQuantity} total</p> {/* Display quantity */}
                    </div>
                    <p className='text-lg  font-bold'>Total : {settings?.currencyName}{displayPrice(totalPrice)}</p>
                </div>

                <div className="w-full flex flex-row justify-center md:justify-end" style={{ fontSize: '0.8rem', gap: '4px' }}>
                    {/* <div className="flex justify-between"> */}


                    <button onClick={handleNewSession} className='NewBtn'>
                        New Session
                    </button>

                    <button onClick={handleClearCart} className='ClrBtn'>
                        Clear Cart
                    </button>

                    <button onClick={handleCheckout}
                        className={`${!canCheckout ? 'bg-gray-600' : 'bg-red-600'} ActionBtn`}
                        disabled={!canCheckout}
                    >
                        Send Order
                    </button>

                    {/* </div> */}
                </div>
            </div>

            {/* Reuse ProductOptionsModal for editing */}
            {modalOpen && selectedCartItem && (
                <ProductOptionsModal
                    product={selectedCartItem.product} // Pass the entire product object
                    vendorId={selectedCartItem.vendorId} // Pass the vendorId from the selected cart item
                    isOpen={modalOpen}
                    onClose={handleCloseModal}
                    initialQuantity={selectedCartItem.quantity}
                    cartItem={selectedCartItem} // Pass the cart item to modal for editing
                    isEditMode={true} // Enable edit mode in modal
                    fromCart={true} // Enable edit mode if from Cart

                />
            )}
        </div>
    );
};

export default CartPage;
